<template>
    <b-modal
        title-tag="h2"
        title="Send SMS"
        :lazy="true"
        :visible="isOpen"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-alert :show="isShowWarningChangeNumber" variant="warning">
            <i class="cus-error" /> The mobile number provided is different than
            the one that is currently on file. Sending this message will update
            the {{ module }}'s mobile number. Click "Send" to continue.
        </b-alert>

        <b-form-group label-cols="3" label="Patient(s):" label-for="patient">
            <vue-select
                v-model="patient"
                label="name"
                :placeholder="patientPlaceholder"
                :disabled="isLoadingList"
                :options="patientOptions"
                :clearable="true"
                class="show-actions"
                multiple
            >
                <template v-slot:open-indicator>
                    <span></span>
                </template>
            </vue-select>
        </b-form-group>

        <b-form-group label-cols="3" label="Mobile:" label-for="mobile">
            <b-row>
                <b-col cols="4" class="pr-0">
                    <country-code
                        class="form-control"
                        v-model="mobileCountryCode"
                        :defaultValue="$root.setting.phone_code"
                    />
                </b-col>
                <b-col class="pl-1">
                    <b-form-input
                        v-model="mobileNumber"
                        :class="{
                            'border-danger': formError.includes('mobileNumber')
                        }"
                        placeholder="Mobile (required)"
                    ></b-form-input>
                </b-col>
            </b-row>
        </b-form-group>

        <b-form-group label-cols="3" label="Message:" label-for="message">
            <editable
                v-model="message"
                placeholder="Message (required)"
                :defaultHeight="82"
                :maxlength="maxCharacters"
                :class="{
                    'border-danger': formError.includes('message')
                }"
            />
            <p class="remaining-chars mb-0">
                {{ remainingChars }} character(s) remaining
            </p>
            <p
                class="remaining-chars my-0 cursor-pointer text-primary"
                v-if="smsCount && smsCount > 1"
                @click="isShowSMSExplanation = !isShowSMSExplanation"
            >
                {{ smsCount }} sms combined into single message
            </p>
        </b-form-group>

        <b-alert :show="isShowSMSExplanation && smsCount > 1" variant="warning">
            <i class="cus-error" /> Messages longer than 150 characters will be
            billed as multiple SMS messages but delivered to customer as one
            message.
        </b-alert>

        <template v-slot:modal-footer>
            <loader v-if="isSending" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSending"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSending" variant="primary" @click="send">
                Send
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import ClientDocument from "@/services/client-documents";
import Communication from "@/services/communications";
import ClientServices from "@/services/client";
import vueSelect from "vue-select";
import CountryCode from "@/module/commons/countrycode.vue";
import Loader from "@/module/commons/loader";
import ContactServices from "@/services/contacts";
import COMMUNICATION from "@/constants/communications";
import ContactRelationship from "@/services/contact-relationships";
import { filter, uniqBy, find } from "lodash";
import PatientHelper from "@/mixins/patient";
import Editable from "@/components/elements/editable-textarea";

export default {
    mixins: [PatientHelper],
    components: {
        vueSelect,
        CountryCode,
        Loader,
        Editable
    },
    props: {
        module: {
            String,
            required: true,
            validator: function (value) {
                // The value must match one of these strings
                return (
                    [
                        COMMUNICATION.MODULE_CLIENT,
                        COMMUNICATION.MODULE_CONTACT
                    ].indexOf(value) !== -1
                );
            }
        },
        contactDetails: {
            type: Object
        },
        clientDetails: {
            type: Object
        }
    },
    data() {
        return {
            communication: null, // if not null, then this is resend communication
            patient: [],
            mobileCountryCode: "",
            mobileNumber: "",
            message: "Msg from DaySmart Vet.",
            COMMUNICATION: COMMUNICATION,
            isOpen: false,
            isSending: false,
            isLoadingList: true,
            patientOptions: [],
            maxCharacters: 1600,
            originalPhoneNumber: "",
            isShowWarningChangeNumber: false,
            isShowSMSExplanation: false,
            formError: []
        };
    },
    computed: {
        remainingChars() {
            return this.maxCharacters - this.message.length;
        },
        patientPlaceholder() {
            return this.isLoadingList ? "Loading..." : "Patient(s) (optional)";
        },
        currentPhoneNumber() {
            return `${this.mobileCountryCode}${this.mobileNumber}`;
        },
        smsCount() {
            if (!this._isExtensionEnabled("SMS")) {
                return null;
            }
            return this.message.length > 0
                ? Math.ceil(this.message.length / 150)
                : 1;
        }
    },
    watch: {
        mobileNumber(value) {
            this.removeError(value, "mobileNumber");
        },
        message(value) {
            this.removeError(value, "message");
        }
    },
    methods: {
        open(communication) {
            this.communication = communication || null;

            if (this.module == COMMUNICATION.MODULE_CLIENT) {
                this.fetchPatientListClient(this.clientDetails.id);
                this.mobileNumber = this.clientDetails.mobile;
                this.mobileCountryCode = this.clientDetails.mobile_country_code;
                this.originalPhoneNumber = `${this.clientDetails.mobile_country_code}${this.clientDetails.mobile}`;
            } else {
                this.fetchPatientListContact(this.contactDetails.id);
                this.mobileNumber = this.contactDetails.mobile;
                this.mobileCountryCode = this.contactDetails.mobile_country_code;
                this.originalPhoneNumber = `${this.contactDetails.mobile_country_code}${this.contactDetails.mobile}`;
            }

            this.isOpen = true;
            if (communication) {
                this.message = communication.content;
            } else {
                this.message = this._isExtensionEnabled("2_WAY_MESSAGING")
                    ? ""
                    : "Msg from " +
                      this.$store.state.setting.business.name +
                      ".";
            }
        },
        close() {
            this.isOpen = false;
        },
        async fetchPatientListClient(clientId) {
            this.isLoadingList = true;
            this.patientOptions = await ClientDocument.getPatient(clientId);
            this.setPatient();
            this.isLoadingList = false;
        },
        async fetchPatientListContact(contactId) {
            let isCompany = this.$store.state.contact.isCompany;
            this.isLoadingList = true;
            if (isCompany) {
                await ContactRelationship.getCompanyRelationship(contactId, {
                    perPage: 50
                })
                    .then(res => {
                        this.filterContactRelationships(res);
                    })
                    .finally(() => {
                        this.isLoadingList = false;
                    });
            } else {
                await ContactRelationship.getContactRelationship(contactId, {
                    perPage: 50
                })
                    .then(res => {
                        this.filterContactRelationships(res);
                    })
                    .finally(() => {
                        this.isLoadingList = false;
                    });
            }
        },
        filterContactRelationships(res) {
            let self = this;
            let filteredPatients = filter(res.resources, patient => {
                patient.name = patient.name + " " + self.getStatus(patient);
                return patient.entity_type == "patient";
            });
            this.patientOptions = uniqBy(filteredPatients, "id");
            this.setPatient();
        },
        setPatient() {
            if (this.communication && this.communication.patient_id) {
                const existingPatient = find(this.patientOptions, {
                    id: this.communication.patient_id
                });
                if (existingPatient) {
                    this.patient = [existingPatient];
                }
            }
        },
        async send() {
            this.formError = [];
            let updatedModule = false;
            let moduleId =
                this.module == COMMUNICATION.MODULE_CLIENT
                    ? this.clientDetails.id
                    : this.contactDetails.id;
            try {
                if (this.mobileNumber === "") {
                    this.formError.push("mobileNumber");
                    throw new Error("Mobile number is required");
                }

                if (this.message === "") {
                    this.formError.push("message");
                    throw new Error("Message is required");
                }

                if (
                    this.currentPhoneNumber !== this.originalPhoneNumber &&
                    this.isShowWarningChangeNumber === false
                ) {
                    this.isShowWarningChangeNumber = true;
                    return; // stop sending
                }

                this.isSending = true;

                let params = {
                    to: this._formatPhone(
                        this.mobileNumber,
                        this.mobileCountryCode
                    ),
                    message: this.message,
                    type: 2,
                    ref_id: moduleId,
                    business_id: this.$store.state.setting.business.id,
                    communication_data: {
                        date: Math.round(this._now().toSeconds()),
                        type: 6,
                        reference_type:
                            COMMUNICATION.REFERENCE_TYPE[this.module],
                        content: this.message,
                        patients: this.patient.map(patient => patient.id),
                        reference_id: moduleId,
                        business_id: this.$store.state.setting.business.id,
                        reminder_actor_id: null,
                        is_system_generated: false
                    }
                };

                if (this.module == COMMUNICATION.MODULE_CLIENT) {
                    params.communication_data.client_id = this.clientDetails.id;
                } else if (this.module == COMMUNICATION.MODULE_CONTACT) {
                    params.communication_data.contact_id = this.contactDetails.id;
                }

                // params above is ready, so can just use params
                let sent = true;

                if (this.communication) {
                    // For Resend SMS
                    // set the reference type & reference id
                    params.communication_data.reference_type =
                        COMMUNICATION.REFERENCE_TYPE["resent_communication"];
                    params.communication_data.reference_id = this.communication.id;
                }

                // For send new SMS
                sent = await Communication.sendSMS(params);
                if (sent && this.isShowWarningChangeNumber) {
                    if (this.module == COMMUNICATION.MODULE_CLIENT) {
                        updatedModule = ClientServices.update(
                            this.clientDetails.id,
                            {
                                id: this.clientDetails.id,
                                firstname: this.clientDetails.firstname,
                                lastname: this.clientDetails.lastname,
                                mobile: this.mobileNumber,
                                mobile_country_code: this.mobileCountryCode
                            }
                        );
                    } else {
                        updatedModule = ContactServices.updateContactActor({
                            id: this.contactDetails.id,
                            firstname: this.contactDetails.firstname,
                            lastname: this.contactDetails.lastname,
                            mobile: this.mobileNumber,
                            mobile_country_code: this.mobileCountryCode
                        });
                    }
                }

                if (sent) {
                    this.isSending = false;
                    this.close();
                    // Post send SMS, to update the client phone number
                    this.$emit("sent", this.isShowWarningChangeNumber, {
                        mobileNumber: this.mobileNumber,
                        mobileCountryCode: this.mobileCountryCode
                    });

                    this._message(
                        updatedModule
                            ? "SMS message successfully sent and mobile number updated."
                            : "SMS message successfully sent."
                    );
                    this.$emit("onsuccess");
                } else {
                    throw new Error("Error: Unable to send SMS");
                }
            } catch (e) {
                this.isSending = false;
                this._message(e.message, "danger");
            }
        },
        removeError(model, value) {
            if (value.length > 0) {
                this.formError = this.formError.filter(err => err !== model);
            }
        }
    }
};
</script>

<style scoped>
.remaining-chars {
    color: grey;
    margin-top: 5px;
    font-size: 10px;
}
</style>
